import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { campaignsAPI } from '../../services/api';
import {
  ArrowLeft, Play, Pause, Square, Download, Phone, Clock, Users,
  CheckCircle, XCircle, PhoneMissed, PhoneOff, Loader2, RefreshCw,
  TrendingUp, Timer, AlertCircle, Ban
} from 'lucide-react';

const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { clientUser } = useAuth();
  
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const fetchCampaign = useCallback(async () => {
    if (!clientUser?.id) return;
    
    try {
      const response = await campaignsAPI.get(clientUser?.id, campaignId);
      if (response.data.success) {
        setCampaign(response.data.campaign);
      }
    } catch (err) {
      console.error('Error fetching campaign:', err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, clientUser?.id]);

  useEffect(() => {
    if (!clientUser?.id) return;
    
    fetchCampaign();
    const interval = setInterval(() => {
      if (campaign?.status === 'running') {
        fetchCampaign();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchCampaign, campaign?.status, clientUser?.id]);

  const handleControl = async (action) => {
    try {
      await campaignsAPI.control(clientUser?.id, campaignId, action);
      fetchCampaign();
    } catch (err) {
      console.error(`Error ${action} campaign:`, err);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await campaignsAPI.export(clientUser?.id, campaignId);
      
      if (response.data.success) {
        const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error exporting:', err);
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'paused': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'answered': return 'bg-green-500/20 text-green-400';
      case 'no_answer': return 'bg-yellow-500/20 text-yellow-400';
      case 'busy': return 'bg-orange-500/20 text-orange-400';
      case 'failed': return 'bg-red-500/20 text-red-400';
      case 'pending': return 'bg-gray-500/20 text-gray-400';
      case 'dialing': return 'bg-blue-500/20 text-blue-400';
      case 'dnc_blocked': return 'bg-orange-500/20 text-orange-400';
      case 'outside_hours': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getContactStatusIcon = (status) => {
    switch (status) {
      case 'answered':
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'no_answer':
        return <PhoneMissed className="w-4 h-4 text-yellow-400" />;
      case 'busy':
        return <PhoneOff className="w-4 h-4 text-orange-400" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'dialing':
        return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
      case 'dnc_blocked':
        return <Ban className="w-4 h-4 text-orange-400" />;
      default:
        return <Phone className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Campaign Not Found</h2>
        <button
          onClick={() => navigate('/client/campaigns')}
          className="text-teal-400 hover:underline"
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  const stats = campaign.stats || {};
  const total = campaign.total_contacts || 1;
  const completed = (stats.completed || 0) + (stats.answered || 0) + (stats.no_answer || 0) + (stats.busy || 0) + (stats.failed || 0);
  // Cap progress at 100%
  const progress = Math.min(100, Math.round((completed / total) * 100));

  const filteredContacts = campaign.contacts?.filter(c => {
    if (activeTab === 'all') return true;
    return c.status === activeTab;
  }) || [];

  return (
    <div className="space-y-6" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/client/campaigns')}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{campaign.name}</h1>
              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(campaign.status)}`}>
                {campaign.status}
              </span>
            </div>
            <p className="text-gray-400">{campaign.description || 'No description'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {campaign.status === 'draft' && (
            <button
              onClick={() => handleControl('start')}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-black font-medium transition-colors"
            >
              <Play className="w-4 h-4" />
              Start
            </button>
          )}
          {campaign.status === 'running' && (
            <button
              onClick={() => handleControl('pause')}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg text-black font-medium transition-colors"
            >
              <Pause className="w-4 h-4" />
              Pause
            </button>
          )}
          {campaign.status === 'paused' && (
            <button
              onClick={() => handleControl('start')}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-black font-medium transition-colors"
            >
              <Play className="w-4 h-4" />
              Resume
            </button>
          )}
          {(campaign.status === 'running' || campaign.status === 'paused') && (
            <button
              onClick={() => handleControl('stop')}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white font-medium transition-colors"
            >
              <Square className="w-4 h-4" />
              Stop
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export CSV
          </button>
          <button
            onClick={fetchCampaign}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            Total
          </div>
          <p className="text-2xl font-bold text-white">{campaign.total_contacts}</p>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Phone className="w-4 h-4" />
            Dialed
          </div>
          <p className="text-2xl font-bold text-white">{stats.dialed || 0}</p>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
            <CheckCircle className="w-4 h-4" />
            Answered
          </div>
          <p className="text-2xl font-bold text-green-400">{stats.answered || 0}</p>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-yellow-400 text-sm mb-1">
            <PhoneMissed className="w-4 h-4" />
            No Answer
          </div>
          <p className="text-2xl font-bold text-yellow-400">{stats.no_answer || 0}</p>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-400 text-sm mb-1">
            <XCircle className="w-4 h-4" />
            Failed
          </div>
          <p className="text-2xl font-bold text-red-400">{stats.failed || 0}</p>
        </div>
        {(stats.dnc_blocked || 0) > 0 && (
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-orange-400 text-sm mb-1">
              <Ban className="w-4 h-4" />
              DNC Blocked
            </div>
            <p className="text-2xl font-bold text-orange-400">{stats.dnc_blocked || 0}</p>
          </div>
        )}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-blue-400 text-sm mb-1">
            <Timer className="w-4 h-4" />
            Total Time
          </div>
          <p className="text-2xl font-bold text-blue-400">
            {Math.floor((stats.total_duration || 0) / 60)}m {(stats.total_duration || 0) % 60}s
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-400">Progress</span>
          <span className="text-white font-medium">{progress}% ({completed}/{total})</span>
        </div>
        <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Campaign Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-sm mb-1">AI Agent</p>
          <p className="text-white font-medium">{campaign.agent_name}</p>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-sm mb-1">Caller ID</p>
          <p className="text-white font-mono">{campaign.from_number}</p>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-sm mb-1">Settings</p>
          <p className="text-white text-sm">
            {campaign.delay_between_calls}s delay • {campaign.max_concurrent_calls} concurrent
          </p>
        </div>
      </div>

      {/* Calling Hours Info (if enabled) */}
      {campaign.calling_hours?.enabled && (
        <div className="bg-[#0d1117] border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-blue-400 mb-2">
            <Clock className="w-4 h-4" />
            <p className="font-medium">Timezone-Aware Calling Enabled</p>
          </div>
          <p className="text-gray-300 text-sm">
            Contacts are only called between <span className="text-white font-medium">{campaign.calling_hours.start_time}</span> and{' '}
            <span className="text-white font-medium">{campaign.calling_hours.end_time}</span> (their local time) on{' '}
            {campaign.calling_hours.days_of_week?.length === 7 
              ? 'all days'
              : campaign.calling_hours.days_of_week?.map(d => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d]).join(', ')
            }. Default timezone: {campaign.calling_hours.default_timezone}
          </p>
        </div>
      )}

      {/* Contacts Table */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Contacts</h2>
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'answered', 'no_answer', 'failed', 'dnc_blocked', 'outside_hours'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-teal-400/20 text-teal-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab === 'dnc_blocked' ? 'DNC Blocked' : 
                 tab === 'outside_hours' ? 'Outside Hours' :
                 tab.charAt(0).toUpperCase() + tab.slice(1).replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
        
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400 font-medium">Phone</th>
                <th className="px-4 py-3 text-left text-gray-400 font-medium">Name</th>
                <th className="px-4 py-3 text-left text-gray-400 font-medium">Status</th>
                <th className="px-4 py-3 text-left text-gray-400 font-medium">Duration</th>
                <th className="px-4 py-3 text-left text-gray-400 font-medium">Attempts</th>
                <th className="px-4 py-3 text-left text-gray-400 font-medium">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact, i) => (
                <tr key={contact.id || i} className="border-t border-gray-800/50 hover:bg-gray-900/30">
                  <td className="px-4 py-3">
                    <span className="text-white font-mono">{contact.phone}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">{contact.name || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getContactStatusIcon(contact.status)}
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(contact.status)}`}>
                        {contact.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {contact.call_duration ? `${contact.call_duration}s` : '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{contact.attempts || 0}</td>
                  <td className="px-4 py-3 text-gray-400">{contact.outcome || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetailPage;
